import { useState, useMemo, useEffect, useRef } from 'react';
import { buildSortedImages, enrichImagesPreservingOrder, getUniqueImageTypes, isWebGLSupported, isIframe } from "../../components/carousel/carouselHelpers";
import { CameraDirection } from "../../components/carousel/cameraTagPosition";

export const useCarousel = (images: any[], cameraDirection: CameraDirection = "cw", preserveOrder: boolean = false) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(-1);
  const [carouselAssetId, setCarouselAssetId] = useState('');
  const [inModal, setInModal] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUcViewerActive, setIsUcViewerActive] = useState(false);
  const [ucTime, setUcTime] = useState(0);
  const [ucImageSrc, setUcImageSrc] = useState("");
  const [ucAudioSrc, setUcAudioSrc] = useState<string>("");
  const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);

  const isIframeMode = useMemo(() => isIframe(), []);
  const isWebGL = useMemo(() => isWebGLSupported(), []);
  const sortedImages = useMemo(
    () => (preserveOrder
      ? enrichImagesPreservingOrder(images, cameraDirection)
      : buildSortedImages(images, cameraDirection)),
    [images, cameraDirection, preserveOrder],
  );
  const uniqueImageTypes = useMemo(() => getUniqueImageTypes(sortedImages), [sortedImages]);

  const filteredImages = useMemo(() => {
    if (selectedType === "all") return sortedImages;
    return sortedImages.filter((image) => image.imageType === selectedType);
  }, [selectedType, sortedImages]);

  const selectedImage = useMemo(() => {
    if (selectedImageIndex < 0 || selectedImageIndex >= filteredImages.length) return null;
    return filteredImages[selectedImageIndex];
  }, [filteredImages, selectedImageIndex]);

  // For mixed galleries (e.g. iframe "all" view) we collapse the two UC videos
  // into a single navigable slot. navigableIndexes lists the filteredImages
  // indexes that participate in thumbnail rendering and arrow-key navigation —
  // only the first UC entry survives.
  const navigableIndexes = useMemo(() => {
    let firstUcSeen = false;
    return filteredImages.reduce<number[]>((acc, image, idx) => {
      const isUc = ["img_uc", "img_uc_dewarp"].includes(image?.imageType || '');
      if (isUc) {
        if (!firstUcSeen) {
          firstUcSeen = true;
          acc.push(idx);
        }
      } else {
        acc.push(idx);
      }
      return acc;
    }, []);
  }, [filteredImages]);

  const effectiveSelectedIndex = useMemo(() => {
    if (selectedImageIndex < 0) return selectedImageIndex;
    const selected = filteredImages[selectedImageIndex];
    if (!selected) return selectedImageIndex;
    const isUc = ["img_uc", "img_uc_dewarp"].includes(selected.imageType || '');
    if (!isUc) return selectedImageIndex;
    return filteredImages.findIndex(img => ["img_uc", "img_uc_dewarp"].includes(img?.imageType || ''));
  }, [filteredImages, selectedImageIndex]);

  useEffect(() => {
    if (filteredImages.length === 0) return;
    const isUcCategorySelected = ["img_uc", "img_uc_dewarp"].includes(selectedType);
    
    const isUcOnlyData = filteredImages.every(img =>
      ["img_uc", "img_uc_dewarp"].includes(img.imageType || '')
    );

    const shouldStayInSyncedMode = isUcCategorySelected || isUcOnlyData;

    if (selectedImageIndex === -1) {
      if (!shouldStayInSyncedMode) {
        // If it's a normal photo gallery (Exterior/All), snap to the first image
        setSelectedImageIndex(0);
      } else {
        // It's UC! Stay at -1 so MainDisplay shows VideoFrameScrubber
        console.log("Staying in Synced Mode (-1) for Undercarriage");
      }
    }
  }, [filteredImages, selectedType, selectedImageIndex]);


  const handleGoTo = (idx: number) => {
    setSelectedImageIndex(idx);
    setIsPlaying(false);
    setIsUcViewerActive(false);
    setUcImageSrc("");
    requestAnimationFrame(() => {
      thumbnailRefs.current[idx]?.scrollIntoView({ inline: 'center', block: 'nearest' });
    });
  };

  const handleSetSelectedType = (type: string) => {
    setSelectedType(type);
    setSelectedImageIndex(-1); // Reset to synced view on every category switch
    setIsPlaying(false);
  };

  const handleImageClick = (image: any) => {
    const isPano = ["img_bgr_pano", "img_pano"].includes(image?.imageType || '');
    if (isPano && isWebGL) return;
    setInModal(true);
    const isUC = ["img_uc_dewarp", "img_uc"].includes(image?.imageType || '');
    setIsUcViewerActive(isUC);
    if (!isUC) setUcImageSrc("");

    if (isIframeMode) {
      requestAnimationFrame(() => {
        const el = document.getElementById("fullscreen-viewer");
        if (el && el.requestFullscreen) {
          el.requestFullscreen().catch(err => {
            console.warn("Fullscreen failed", err);
          });
        }
      });
    }
  };

  const handleTogglePlay = () => setIsPlaying(prev => !prev);

  const handleCloseViewer = () => {
    setInModal(false);
    setIsUcViewerActive(false);
  };

  useEffect(() => {
    if (!isPlaying || navigableIndexes.length <= 1) return;
    const interval = setInterval(() => {
      setSelectedImageIndex(prev => {
        const isUc = ["img_uc", "img_uc_dewarp"].includes(filteredImages[prev]?.imageType || '');
        const lookup = isUc
          ? filteredImages.findIndex(img => ["img_uc", "img_uc_dewarp"].includes(img?.imageType || ''))
          : prev;
        const currentNavPos = navigableIndexes.indexOf(lookup);
        const nextPos = currentNavPos === -1 ? 0 : (currentNavPos + 1) % navigableIndexes.length;
        return navigableIndexes[nextPos];
      });
    }, 900);
    return () => clearInterval(interval);
  }, [isPlaying, navigableIndexes, filteredImages]);

  useEffect(() => {
    const ucUrls = filteredImages
      .filter(img => img.imageType?.includes("uc"))
      .map(img => img.src);

    const left = ucUrls.find(u => u.toLowerCase().includes("left"));
    const nextAudio = left || ucUrls[0] || "";

    // avoid pointless rerenders
    if (nextAudio && nextAudio !== ucAudioSrc) {
      setUcAudioSrc(nextAudio);
    }
  }, [filteredImages, ucAudioSrc, setUcAudioSrc]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditableTarget = !!target?.closest('input, textarea, select, [contenteditable="true"]');
      const canNavigate =
        (inModal || isIframeMode) &&
        !isUcViewerActive &&
        filteredImages.length > 1 &&
        selectedImageIndex >= 0;

      if (!canNavigate || isEditableTarget) return;

      if (navigableIndexes.length === 0) return;
      const currentNavPos = navigableIndexes.indexOf(effectiveSelectedIndex);
      const startPos = currentNavPos === -1 ? 0 : currentNavPos;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextPos = (startPos + 1) % navigableIndexes.length;
        handleGoTo(navigableIndexes[nextPos]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevPos = (startPos - 1 + navigableIndexes.length) % navigableIndexes.length;
        handleGoTo(navigableIndexes[prevPos]);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [filteredImages.length, inModal, isIframeMode, isUcViewerActive, selectedImageIndex, navigableIndexes, effectiveSelectedIndex]);

  return {
    // State
    selectedImageIndex,
    selectedImage,
    inModal,
    selectedType,
    isPlaying,
    isUcViewerActive,
    ucTime,
    ucImageSrc,
    ucAudioSrc,
    thumbnailRefs,
    isWebGL,
    uniqueImageTypes,
    filteredImages,
    sortedImages,
    carouselAssetId,
    navigableIndexes,
    effectiveSelectedIndex,
    // Methods
    setSelectedType: handleSetSelectedType,
    setCarouselAssetId,
    handleGoTo,
    handleImageClick,
    handleCloseViewer,
    handleTogglePlay,
    setUcTime,
    setUcImageSrc,
    setUcAudioSrc,
    setInModal,
    setIsUcViewerActive
  };
};
